import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Swal from "sweetalert2";
import {
  MDBBadge,
  MDBBtn,
  MDBBtnGroup,
  MDBCard,
  MDBCardBody,
  MDBIcon,
  MDBTable,
} from "mdbreact";
import { GenericProducts } from "../../../../../constants";
import { axioKit } from "../../../../../services/utilities";
import TableRowCount from "../../../../../components/pagination/rows";
import Pagination from "../../../../../components/pagination";
import BrandModal from "./modal";
import { packLabel, variantsOf } from "../products/common";
import { isTestsOf } from "../products/packagesEditor";

const url = "commerce/merchandise/products";
const SUPPLIERS_URL = "assets/providers/filterByCategory";

const supplierName = (suppliers = [], id) => {
  const supplier = suppliers.find(({ _id }) => _id === (id?._id || id));
  return supplier ? supplier.name || supplier.displayname : "";
};

const FILTERS = [
  { key: "", label: "All" },
  { key: "registered", label: "Registered" },
  { key: "none", label: "Not registered" },
];

/**
 * Brands registered for a generic, shown when the generic is expanded.
 */
const BrandTable = ({ generic, brands, suppliers, onEdit, onDelete }) => (
  <MDBTable small hover className="mb-2 bg-white">
    <thead className="grey lighten-4">
      <tr>
        <th>Brand</th>
        <th>Manufacturer</th>
        <th>Supplier</th>
        <th>Variants</th>
        <th className="text-center" style={{ width: 90 }}>
          Action
        </th>
      </tr>
    </thead>
    <tbody>
      {!brands.length && (
        <tr>
          <td colSpan={5} className="text-center text-muted small py-3">
            No brand registered yet for {generic?.name || "this generic"}.
          </td>
        </tr>
      )}
      {brands.map((product) => {
        const variants = variantsOf(product);
        const missingTests =
          generic?.uom === "test" &&
          variants.some((variant) => !isTestsOf(variant, product));
        return (
          <tr key={product._id}>
            <td className="align-middle">
              <strong>{product.name}</strong>
              {product.isDefault && (
                <MDBBadge
                  color="primary"
                  className="ml-1"
                  title="Default brand: pre-selected when tagging"
                >
                  <MDBIcon icon="star" className="mr-1" />
                  Default
                </MDBBadge>
              )}
              {missingTests && (
                <MDBBadge
                  color="danger"
                  className="ml-1"
                  title="Set the tests per unit"
                >
                  No test count
                </MDBBadge>
              )}
              {product.tracksLot && (
                <MDBBadge
                  color="info"
                  className="ml-1"
                  title="Lot number and expiry are required when receiving"
                >
                  Lot &amp; expiry
                </MDBBadge>
              )}
            </td>
            <td className="align-middle">{product.manufacturer}</td>
            <td className="align-middle small">
              {supplierName(suppliers, product.supplier) || (
                <span className="text-muted">—</span>
              )}
            </td>
            <td className="align-middle">
              {variants.map((variant, index) => (
                <div key={variant._id || index} className="small">
                  {variant.isDefault && (
                    <MDBIcon
                      icon="star"
                      className="text-warning mr-1"
                      title="Default variant"
                    />
                  )}
                  <b>{variant.name}</b>
                  {packLabel(variant, product) && (
                    <span className="text-muted">
                      {" "}
                      · {packLabel(variant, product)}
                    </span>
                  )}
                </div>
              ))}
            </td>
            <td className="text-center align-middle py-0">
              <MDBBtnGroup>
                <MDBBtn
                  size="sm"
                  color="info"
                  className="px-2"
                  title="Edit"
                  onClick={() => onEdit(product)}
                >
                  <MDBIcon icon="pen" />
                </MDBBtn>
                <MDBBtn
                  size="sm"
                  color="danger"
                  className="px-2"
                  title="Delete"
                  onClick={() => onDelete(product)}
                >
                  <MDBIcon icon="trash" />
                </MDBBtn>
              </MDBBtnGroup>
            </td>
          </tr>
        );
      })}
    </tbody>
  </MDBTable>
);

/**
 * Generic products of the branch, each collapsible to its registered brands.
 * generic -> brand (+ variants) -> tagging -> purchase -> dispense
 */
export default function BrandedProducts() {
  const {
      token,
      activePlatform,
      maxPage = 5,
    } = useSelector(({ auth }) => auth),
    [products, setProducts] = useState([]),
    // Sources > Suppliers of the branch, for the preferred supplier of a brand
    [suppliers, setSuppliers] = useState([]),
    [search, setSearch] = useState(""),
    [filter, setFilter] = useState(""),
    [section, setSection] = useState(""),
    [expanded, setExpanded] = useState({}),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState(null),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;

  const notify = useCallback(
    (message, appearance = "error") =>
      addToast(message, { appearance, autoDismiss: true }),
    [addToast],
  );

  // reloaded after every change since the server moves the default brand
  const load = useCallback(() => {
    if (!branch || !token) return Promise.resolve();
    setIsLoading(true);
    return Promise.all([
      axioKit.universal(`${url}/browse`, token, { branch }),
      axioKit
        .universal(SUPPLIERS_URL, token, {
          clients: branch,
          category: "supplier",
        })
        .catch(() => ({ payload: [] })),
    ])
      .then(([_products, _suppliers]) => {
        setProducts(_products?.payload || []);
        setSuppliers(
          (_suppliers?.payload || []).filter(({ deletedAt }) => !deletedAt),
        );
      })
      .catch((error) => notify(error.message))
      .finally(() => setIsLoading(false));
  }, [branch, token, notify]);

  useEffect(() => {
    load();
  }, [load]);

  // brands per generic; brands without a known generic are kept apart
  const { byGeneric, unlinked } = useMemo(
    () =>
      products.reduce(
        (result, product) => {
          if (GenericProducts.find(product.pgid))
            (result.byGeneric[product.pgid] =
              result.byGeneric[product.pgid] || []).push(product);
          else result.unlinked.push(product);
          return result;
        },
        { byGeneric: {}, unlinked: [] },
      ),
    [products],
  );

  const sections = useMemo(
    () =>
      [...new Set(GenericProducts.active.map(({ section }) => section))].sort(),
    [],
  );

  const rows = useMemo(() => {
    const key = search.trim().toLowerCase();
    // retired generics only show when they still have brands
    return GenericProducts.collections
      .filter(({ id, retired }) => !retired || byGeneric[id]?.length)
      .filter(({ section: s }) => !section || s === section)
      .filter(({ id }) => {
        const count = byGeneric[id]?.length || 0;
        return !filter || (filter === "registered" ? count > 0 : count === 0);
      })
      .filter(
        ({ id, name = "", abbreviation = "", subname = "" }) =>
          !key ||
          [name, abbreviation, subname].some((value) =>
            value.toLowerCase().includes(key),
          ) ||
          (byGeneric[id] || []).some(
            ({ name: brand = "", manufacturer = "" }) =>
              [brand, manufacturer].some((value) =>
                value.toLowerCase().includes(key),
              ),
          ),
      );
  }, [byGeneric, search, filter, section]);

  // searching a brand opens its generic
  const isSearching = search.trim().length > 0;

  const totalPages = Math.ceil(rows.length / maxPage) || 1;
  const activePage = Math.min(page, totalPages);
  const paginated = rows.slice(
    (activePage - 1) * maxPage,
    activePage * maxPage,
  );

  const registered = Object.keys(byGeneric).length;

  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  const resetPage = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleSubmit = async (data) => {
    setIsLoading(true);
    try {
      const { success } = data._id
        ? await axioKit.update(url, data, token)
        : await axioKit.save(url, { ...data, branch }, token);
      notify(success, "success");
      setSelected(null);
      // keep the generic of the saved brand open
      setExpanded((prev) => ({ ...prev, [data.pgid]: true }));
      await load();
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = ({ _id, name }) =>
    Swal.fire({
      title: `Delete ${name}?`,
      text: "It can no longer be tagged or dispensed.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async ({ isConfirmed }) => {
      if (!isConfirmed) return;
      try {
        const { success } = await axioKit.destroy(url, { _id }, token);
        notify(success, "success");
        await load();
      } catch (error) {
        notify(error.message);
      }
    });

  return (
    <MDBCard narrow className="pb-3">
      <MDBCardBody>
        {/* header */}
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-2"
          style={{ gap: 8 }}
        >
          <div>
            <h5 className="mb-0">
              <MDBIcon icon="cubes" className="mr-2 text-primary" />
              Products
            </h5>
            <small className="text-muted">
              <b>{registered}</b> of {GenericProducts.active.length} generics
              registered (with a brand) · {products.length} brand(s)
            </small>
            <div
              className="progress mt-1"
              style={{ height: 6, maxWidth: 320 }}
              title={`${Math.round((registered / GenericProducts.active.length) * 100)}% registered`}
            >
              <div
                className="progress-bar bg-success"
                role="progressbar"
                style={{
                  width: `${(registered / GenericProducts.active.length) * 100}%`,
                }}
              />
            </div>
          </div>
          <MDBBtn
            size="sm"
            color="primary"
            className="m-0"
            onClick={() => setSelected({})}
            disabled={!branch}
          >
            <MDBIcon icon="plus" className="mr-1" /> Add brand
          </MDBBtn>
        </div>

        {/* filters */}
        <div
          className="d-flex flex-wrap align-items-center mb-3"
          style={{ gap: 8 }}
        >
          <div className="position-relative" style={{ minWidth: 240, flex: 1 }}>
            <MDBIcon
              icon="search"
              className="position-absolute text-muted"
              style={{ top: "50%", left: 10, transform: "translateY(-50%)" }}
            />
            <input
              className="form-control form-control-sm"
              style={{ paddingLeft: 30 }}
              placeholder="Search generic, brand or manufacturer (e.g. SYR, Terumo)"
              value={search}
              onChange={({ target }) => resetPage(setSearch)(target.value)}
            />
          </div>
          <select
            className="browser-default custom-select custom-select-sm"
            style={{ width: 190 }}
            value={section}
            onChange={({ target }) => resetPage(setSection)(target.value)}
          >
            <option value="">All sections</option>
            {sections.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <div className="btn-group" role="group" aria-label="Brand filter">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key || "all"}
                type="button"
                className={`btn btn-sm m-0 z-depth-0 ${
                  filter === key ? "btn-primary" : "btn-outline-primary"
                }`}
                style={{ textTransform: "none" }}
                onClick={() => resetPage(setFilter)(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* brands without a known generic */}
        {unlinked.length > 0 && (
          <div className="border border-warning rounded mb-3">
            <div
              role="button"
              tabIndex={0}
              className="px-3 py-2 d-flex align-items-center amber lighten-5"
              style={{ cursor: "pointer" }}
              onClick={() => toggle("unlinked")}
              onKeyDown={(e) => e.key === "Enter" && toggle("unlinked")}
            >
              <MDBIcon
                icon={expanded.unlinked ? "chevron-down" : "chevron-right"}
                className="mr-2 text-muted"
              />
              <MDBIcon icon="unlink" className="mr-2 text-warning" />
              <b>No generic</b>
              <small className="text-muted ml-2">
                {unlinked.length} brand(s) cannot be tagged or dispensed until
                linked; edit them to choose their generic.
              </small>
            </div>
            {expanded.unlinked && (
              <div className="px-3 pt-2">
                <BrandTable
                  brands={unlinked}
                  suppliers={suppliers}
                  onEdit={setSelected}
                  onDelete={handleDelete}
                />
              </div>
            )}
          </div>
        )}

        {/* generics */}
        <div className="border rounded">
          {!paginated.length && (
            <p className="text-center text-muted my-4">
              {isLoading ? "Loading..." : "No generic products found."}
            </p>
          )}
          {paginated.map((generic) => {
            const brands = byGeneric[generic.id] || [];
            const isOpen =
              expanded[generic.id] || (isSearching && brands.length > 0);
            return (
              <div key={generic.id} className="border-bottom">
                <div
                  role="button"
                  tabIndex={0}
                  className={`px-3 py-2 d-flex align-items-center ${
                    isOpen ? "blue lighten-5" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => toggle(generic.id)}
                  onKeyDown={(e) => e.key === "Enter" && toggle(generic.id)}
                >
                  <MDBIcon
                    icon={isOpen ? "chevron-down" : "chevron-right"}
                    className="mr-2 text-muted"
                    style={{ width: 12 }}
                  />
                  <code className="mr-2">{generic.abbreviation}</code>
                  <div
                    className="text-truncate mr-2"
                    style={{ minWidth: 0, flex: 1 }}
                  >
                    <b>{generic.name}</b>
                    {generic.subname && (
                      <small className="text-muted"> · {generic.subname}</small>
                    )}
                    <small className="d-block text-muted">
                      {generic.section} · {generic.category} · uom:{" "}
                      {generic.uom}
                    </small>
                  </div>
                  {generic.retired && (
                    <MDBBadge
                      color="light"
                      className="mr-2"
                      title={generic.note}
                    >
                      Retired
                    </MDBBadge>
                  )}
                  <MDBBadge
                    color={generic.reusable ? "light" : "success"}
                    className="mr-2"
                    title={
                      generic.reusable
                        ? "P.O. + monthly dispense by the Chief MLS"
                        : "Declared per test"
                    }
                  >
                    {generic.reusable ? "Reusable" : "Per test"}
                  </MDBBadge>
                  {brands.length ? (
                    <MDBBadge color="primary" style={{ minWidth: 70 }}>
                      {brands.length} brand{brands.length > 1 ? "s" : ""}
                    </MDBBadge>
                  ) : (
                    <MDBBadge
                      color="light"
                      className="text-danger"
                      style={{ minWidth: 70 }}
                    >
                      not registered
                    </MDBBadge>
                  )}
                </div>

                {isOpen && (
                  <div className="px-3 pt-2 pb-1 grey lighten-5">
                    <BrandTable
                      generic={generic}
                      brands={brands}
                      suppliers={suppliers}
                      onEdit={setSelected}
                      onDelete={handleDelete}
                    />
                    {!generic.retired && (
                      <MDBBtn
                        size="sm"
                        color="primary"
                        outline
                        className="ml-0 mt-0 mb-2"
                        onClick={() => setSelected({ pgid: generic.id })}
                      >
                        <MDBIcon icon="plus" className="mr-1" />
                        Add brand for {generic.name}
                      </MDBBtn>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between align-items-center mt-2">
          <TableRowCount disablePageSelect={false} />
          <Pagination
            total={totalPages}
            page={activePage}
            setPage={(action) => {
              const next =
                typeof action === "number"
                  ? action
                  : activePage + (action ? 1 : -1);
              if (next >= 1 && next <= totalPages) setPage(next);
            }}
          />
        </div>
      </MDBCardBody>

      <BrandModal
        show={!!selected}
        toggle={() => setSelected(null)}
        selected={selected}
        onSubmit={handleSubmit}
        suppliers={suppliers}
        isLoading={isLoading}
      />
    </MDBCard>
  );
}
